import React, { useCallback, useState } from "react";
import Section from "../../../components/common/Section";
import ReceiverAddress from "./ReceiverAddress";
import { AddressOption, LocationState } from "../../../types/types";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import FormErrorMessage from "../../../components/common/FormErrorMessage";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
} from "../../../apis/locationApis";
import { IconMapPin } from "@tabler/icons-react";
import { useLanguage } from "../../../context/LanguageContext";

interface ReceiverSectionProps {
  register: any;
  errors: any;
  setValue: any;
  watch: any;
  control: any;
  clearErrors: any;
  setError: any;
  isSidebar?: boolean;
}

// ── Staff-style section header ──────────────────────────────────────────────
const SectionHeader = ({
  title,
  className = "mb-2",
}: {
  icon?: any;
  title: string;
  className?: string;
}) => {
  const { t } = useLanguage();
  return (
    <div className={`mb-2 ${className}`}>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {t(title)}
      </p>
    </div>
  );
};

const ReceiverSection: React.FC<ReceiverSectionProps> = ({
  register,
  errors,
  setValue,
  watch,
  control,
  clearErrors,
  setError,
  isSidebar = false,
}) => {
  const { t } = useLanguage();
  const [receiverLocation, setReceiverLocation] = useState<LocationState>({});
  const [selectedOption, setSelectedOption] = useState<AddressOption>("short");
  const [searchRef, setSearchRef] = useState<string>("");
  const { data: countries = [] } = useFetchCountriesAllData();
  const {
    data: cities = [],
    isPending: listPending,
    refetch: fetchReceiverCities,
  } = useFetchCountryCitiesAllData(
    receiverLocation?.country?.id,
    searchRef,
    selectedOption,
  );

  const handleCitySearch = (query: string) => {
    if (query?.trim().length >= 3) {
      setSearchRef(query);
      fetchReceiverCities();
    }
  };

  const handleCountryChange = useCallback(
    (val: string) => {
      const country = countries.find((c: any) => c.id === val);
      if (!country) return;
      setValue("receiver.dialCode", country?.dialCode);
      setValue("receiver.country", country.id);
      setValue("receiver.countryName", country.name);
      setValue("receiver.latitude", "");
      setValue("receiver.longitude", "");
      setReceiverLocation((prev) => ({
        ...prev,
        country,
        city: undefined,
        district: undefined,
      }));
      setValue("receiver.city", "");
      setValue("receiver.district", "");
      setValue("receiver.cityName", "");
      setValue("receiver.districtName", "");
      if (country.id) fetchReceiverCities();
    },
    [countries, setValue, fetchReceiverCities],
  );

  // Pre-select Saudi Arabia by default on initial load
  const hasSetDefaultCountry = React.useRef(false);
  React.useEffect(() => {
    if (countries.length && !receiverLocation.country && !hasSetDefaultCountry.current) {
      const sa = countries.find((c: any) => c.name.toLowerCase() === "saudi arabia" || c.code === "SA" || c.dialCode === "966");
      if (sa) {
        hasSetDefaultCountry.current = true;
        handleCountryChange(sa.id);
      }
    }
  }, [countries, receiverLocation.country, handleCountryChange]);

  if (isSidebar) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-3">
          <SectionHeader
            icon={IconMapPin}
            title="Receiver Details"
            className="w-full"
          />
          <NewSelectInput
            name="receiver.country"
            label={t("Country")}
            placeholder={t("Select country")}
            className="flex items-center gap-2 w-full"
            selectOptions={countries.map((c: any) => ({
              id: c.id,
              value: c.id,
              text: c.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Required" }}
            error={errors.receiver?.country}
            disabled={!countries.length}
            value={receiverLocation?.country?.id}
            setValue={handleCountryChange}
          />
        </div>

        <div className="grid grid-cols-2 gap-3 relative z-10">
          {/* Country */}
          <NewinputField
            name="receiver.name"
            type="text"
            title={t("Name")}
            register={register}
            error={errors.receiver?.name}
            validation={{
              required: "Required",
              pattern: {
                value: /^(?=.*[A-Za-z\u0600-\u06FF])[A-Za-z\u0600-\u06FF0-9\s-]+$/,
                message: "Must include a letter",
              },
              validate: (v: any) => (!v?.trim() ? "Cannot be empty" : true),
            }}
            className="flex items-center  gap-2 col-span-2"
          />
          {/* Mobile */}
          <div className="flex items-center gap-2">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              {t("Mobile")}<span className="text-red-600 ml-1">*</span>
            </label>
            <div className="w-full">
              <div
                className={`flex items-center rounded-xl border bg-yellow-50 px-4 py-2 ${errors.receiver?.mobile ? "border-red-400" : "border-slate-200"}`}
              >
                <span className="text-gray-500 text-sm whitespace-nowrap border-r border-gray-200 pr-2 mr-2">
                  {watch("receiver.dialCode")
                    ? `+${watch("receiver.dialCode")}`
                    : "+--"}
                </span>
                <input
                  type="number"
                  placeholder={t("Mobile number")}
                  className="w-full outline-none bg-transparent text-sm text-[#1E1E1E] hide-spinner"
                  {...register("receiver.mobile", {
                    required: "Required",
                    pattern: { value: /^\d{9,10}$/, message: "9-10 digits" },
                    validate: (v: string) =>
                      /^0+$/.test(v?.trim()) ? "Cannot be all zeros" : true,
                  })}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setValue(
                      "receiver.mobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10),
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  maxLength={10}
                />
              </div>
              {errors.receiver?.mobile && (
                <FormErrorMessage error={errors.receiver?.mobile} />
              )}
            </div>
          </div>
          <NewinputField
            name="receiver.email"
            type="text"
            title={t("Email")}
            register={register}
            error={errors.receiver?.email}
            validation={{
              required: "Required",
              setValueAs: (v: string) => v?.toLowerCase(),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email",
              },
            }}
            className="flex items-center gap-2"
          />
        </div>

        {/* Name */}

        {/* Email */}

        {/* Address sub-fields (map, city, district, lat/lng, etc.) */}
        <ReceiverAddress
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          clearErrors={clearErrors}
          control={control}
          setError={setError}
          className=""
          cities={cities}
          handleCitySearch={handleCitySearch}
          isCitiesFetchPending={listPending}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          countries={countries}
          onCountryChange={(countryId: string) => {
            handleCountryChange(countryId);
            const c = countries.find((x: any) => x.id === countryId);
            if (c)
              setReceiverLocation((prev) => ({
                ...prev,
                country: c,
                city: undefined,
                district: undefined,
              }));
          }}
          isSidebar={isSidebar}
        />
      </div>
    );
  }

  // ── Non-sidebar: original layout ─────────────────────────────────────────
  return (
    <Section title={t("RECEIVER INFORMATION")}>
      <div className="grid gap-3 grid-cols-6">
        <div className="col-span-6">
          <SelectInput
            name="receiver.country"
            label={t("Country")}
            placeholder={t("Select Country")}
            selectOptions={countries.map((c: any) => ({
              id: c.id,
              value: c.id,
              text: c.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: "Receiver country is required" }}
            error={errors.receiver?.country}
            disabled={!countries.length}
            value={receiverLocation?.country?.id}
            setValue={handleCountryChange}
          />
        </div>
        <Input
          name="receiver.name"
          type="text"
          title={t("Name")}
          register={register}
          error={errors.receiver?.name}
          validation={{
            required: "Receiver name is required",
            pattern: {
              value: /^(?=.*[A-Za-z\u0600-\u06FF])[A-Za-z\u0600-\u06FF0-9\s-]+$/,
              message: "Must include at least one letter",
            },
            validate: (v) => (!v?.trim() ? "Cannot be empty" : true),
          }}
          className="col-span-2"
        />
        <div className="col-span-2">
          <label className="text-[#1E293B] text-sm whitespace-nowrap">
            {t("Mobile")}<span className="text-red-600 ml-1">*</span>
          </label>
          <div
            className={`flex items-center rounded-full bg-white px-2 mt-1 border ${errors.receiver?.mobile ? "border-red-500" : "border-[#D8D8D8]"}`}
          >
            <span className="pr-1 text-gray-700 text-sm whitespace-nowrap">
              {watch("receiver.dialCode")
                ? `+${watch("receiver.dialCode")}`
                : "+--"}
            </span>
            <div className="flex-1 border-l-2">
              <input
                type="number"
                placeholder=""
                className="w-full border-0 outline-none bg-transparent px-2 py-[6px] text-[#1E1E1E] hide-spinner"
                {...register("receiver.mobile", {
                  required: "Mobile number is required",
                  pattern: { value: /^\d{9,10}$/, message: "9 or 10 digits" },
                })}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValue(
                    "receiver.mobile",
                    e.target.value.replace(/\D/g, "").slice(0, 10),
                    { shouldValidate: true, shouldDirty: true },
                  )
                }
                maxLength={10}
              />
            </div>
          </div>
          {errors.receiver?.mobile && (
            <FormErrorMessage error={errors.receiver?.mobile} />
          )}
        </div>
        <Input
          name="receiver.email"
          type="text"
          title={t("Email")}
          register={register}
          error={errors.receiver?.email}
          validation={{
            required: "Receiver email is required",
            setValueAs: (v: string) => v?.toLowerCase(),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email",
            },
          }}
          className="col-span-2"
        />
        <ReceiverAddress
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
          clearErrors={clearErrors}
          control={control}
          setError={setError}
          className="col-span-6"
          cities={cities}
          handleCitySearch={handleCitySearch}
          isCitiesFetchPending={listPending}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          countries={countries}
          onCountryChange={(countryId: string) => {
            handleCountryChange(countryId);
            const c = countries.find((x: any) => x.id === countryId);
            if (c)
              setReceiverLocation((prev) => ({
                ...prev,
                country: c,
                city: undefined,
                district: undefined,
              }));
          }}
        />
      </div>
    </Section>
  );
};

export default ReceiverSection;
